import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ControllAppService } from '../../../../../services/controllApp.service';
import { environment } from '../../../../../../environments/environment';
import { UsuarioService } from '../../../services/usuario.service';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-usuarios',
  standalone: false,
  templateUrl: './usuarios.component.html',
  styleUrl: './usuarios.component.css'
})
export class UsuariosComponent implements OnInit {
  colaboradores: any[] = [];
  filteredColaboradores: any[] = [];
  colaboradorSelecionado: any = null;
  filterType: string = 'nome'; // Default filter type
  searchTerm: string = ''; // Search term for filtering
  previousSearchTerm: string = '';
  previousFilterType: string = 'nome';
  isLoading: boolean = false; // Loading indicator
  sortColumn: string = ''; // Current sort column
  sortDirection: string = 'asc'; // Current sort direction

  // Adicionar variáveis de paginação
  paginaAtual: number = 1;
  itensPorPagina: number = 10;
  totalColaboradores: number = 0;
  totalPaginas: number = 0;
  Math = Math; // Para usar Math.min no template

  // Adicionar propriedade para verificar se o usuário é SuperAdministrador
  isSuperAdmin: boolean = false;
  role: string = ''; // Adicionado para alinhar com EmpresasComponent

  // Novas propriedades para exportação e animação de ícone
  exportMenuAberto = false;
  exportIconAnimando = false;
  exportIconOk = false;

  constructor(
    private controllAppService: ControllAppService,
    private usuarioService: UsuarioService,
    private cdr: ChangeDetectorRef 
  ) { }

  ngOnInit() {
    this.verificarPermissoes(); 
    this.carregarTodosColaboradores();
  }

  verificarPermissoes(): void {
    // Try multiple possible locations for role information
    const roleFromDirect = localStorage.getItem('role');
    const userDataString = localStorage.getItem('userData');
    const usuarioString = localStorage.getItem('usuario');
    // Initialize role as empty string
    this.role = '';
    
    // Try to get role from direct storage
    if (roleFromDirect) {
      this.role = roleFromDirect.trim();
    } 
    // Try to get from userData if direct role not found
    else if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        if (userData && userData.role) {
          this.role = userData.role.trim();
        }
      } catch (error) {
        console.error('UsuariosComponent - Error parsing userData:', error);
      }
    }
    // Try to get from usuario if still not found
    else if (usuarioString) {
      try {
        const usuario = JSON.parse(usuarioString);
        if (usuario && usuario.role) {
          this.role = usuario.role.trim();
        } else if (usuario && usuario.perfil) { // Fallback to 'perfil' if 'role' is not in 'usuario'
          this.role = usuario.perfil.trim();
        }
      } catch (error) {
        console.error('UsuariosComponent - Error parsing usuario:', error);
      }
    }
    
    // Set isSuperAdmin based on the determined role
    this.isSuperAdmin = this.isSuperAdministrador();
  }

  // Helper method to check if user is SuperAdministrador
  isSuperAdministrador(): boolean {
    // Case-insensitive check for "SuperAdministrador" role
    return this.role.toLowerCase() === 'superadministrador';
  }

  carregarTodosColaboradores(): void {
    this.isLoading = true;
    this.usuarioService.carregarTodosColaboradoresPaginado(this.paginaAtual, this.itensPorPagina, this.searchTerm, this.filterType, this.sortColumn, this.sortDirection).subscribe({
      next: (result: { colaboradores: any[], total: number }) => {
        let colaboradores = result.colaboradores;
        this.totalColaboradores = result.total;

        if (!colaboradores || colaboradores.length === 0) {
          this.colaboradores = [];
          this.filteredColaboradores = [];
          this.totalPaginas = 0;
          this.isLoading = false;
          return;
        }

        this.colaboradores = colaboradores.map(c => ({
          ...c,
          empresaNome: c.empresaNome || c.nomeDaEmpresa || c.nomeEmpresa || (c.empresa && c.empresa.nomeDaEmpresa) || 'Não informado',
          matricula: c.matricula || c.numeroMatricula || '-',
          fotoUrl: c.fotoUrl && !c.fotoUrl.startsWith('http') && c.fotoUrl !== 'URL_PADRAO_SEM_IMAGEM'
            ? `${environment.mediaUrl}/images/${c.fotoUrl.split('/').pop()}`
            : (c.fotoUrl === 'URL_PADRAO_SEM_IMAGEM' ? 'https://via.placeholder.com/40x40' : c.fotoUrl || 'https://via.placeholder.com/40x40')
        }));
        this.totalPaginas = Math.ceil(this.totalColaboradores / this.itensPorPagina);

        this.filteredColaboradores = this.colaboradores;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar colaboradores:', err);
        this.colaboradores = [];
        this.filteredColaboradores = [];
        this.totalColaboradores = 0;
        this.totalPaginas = 0;
        this.isLoading = false;
      }
    });
  }

  formatarCPF(cpf: string): string {
    if (!cpf) return '';

    // Remove caracteres não numéricos
    const numerosCPF = cpf.replace(/\D/g, '');

    // Verifica se tem 11 dígitos
    if (numerosCPF.length !== 11) return cpf;

    // Formata para 000.000.000-00
    return numerosCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  applyFilter(): void {
    // Aplicar filtros
    let filtered = this.colaboradores.filter(colaborador => {
      let value = '';
      if (this.filterType === 'empresaNome') {
        value = colaborador.empresaNome?.toString().toLowerCase() || '';
        return value.includes(this.searchTerm.toLowerCase());
      } else if (this.filterType === 'matricula') {
        value = colaborador.matricula?.toString().toLowerCase() || '';
        return value.includes(this.searchTerm.toLowerCase());
      } else if (this.filterType === 'nome') {
        // Se searchTerm está vazio, mostra todos; senão, compara exato
        if (!this.searchTerm) return true;
        return colaborador.nome === this.searchTerm;
      } else {
        value = colaborador[this.filterType]?.toString().toLowerCase() || '';
        return value.includes(this.searchTerm.toLowerCase());
      }
    });

    if (this.sortColumn) {
      filtered.sort((a, b) => {
        const valueA = a[this.sortColumn] !== undefined && a[this.sortColumn] !== null ? a[this.sortColumn].toString().toLowerCase() : '';
        const valueB = b[this.sortColumn] !== undefined && b[this.sortColumn] !== null ? b[this.sortColumn].toString().toLowerCase() : '';

        if (valueA < valueB) {
          return this.sortDirection === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return this.sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    // Atualizar total de colaboradores filtrados e páginas
    this.totalColaboradores = filtered.length;
    this.totalPaginas = Math.ceil(this.totalColaboradores / this.itensPorPagina);

    // Ajustar página atual se necessário
    if (this.paginaAtual > this.totalPaginas && this.totalPaginas > 0) {
      this.paginaAtual = this.totalPaginas;
    }

    // Aplicar paginação
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.filteredColaboradores = filtered.slice(inicio, fim);

    // Adicionar detecção de mudanças para garantir que a UI atualize
    this.cdr.detectChanges();
  }

  abrirModalExclusao(colaborador: any): void {
    this.colaboradorSelecionado = colaborador;
  }

  fecharModalExclusao(): void {
    this.colaboradorSelecionado = null;
  }

  excluirColaborador(): void {
    if (!this.colaboradorSelecionado) return;

    const usuarioIdParaExcluir = this.colaboradorSelecionado.usuarioId || this.colaboradorSelecionado.id;
    if (!usuarioIdParaExcluir) {
      this.fecharModalExclusao();
      return;
    }

    this.controllAppService.deleteById(usuarioIdParaExcluir).subscribe({
      next: () => {
        this.fecharModalExclusao();

        // Remove o colaborador da lista local 'this.colaboradores'
        this.colaboradores = this.colaboradores.filter(c => 
          (c.usuarioId || c.id) !== usuarioIdParaExcluir
        );
        this.applyFilter();
        
      },
      error: (err) => {
        this.fecharModalExclusao();
        console.error('Erro ao excluir colaborador:', err);

      }
    });
  }

  sortBy(column: string): void {
    // If clicking the same column, toggle direction
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.filteredColaboradores.sort((a, b) => {
      const valueA = a[column] !== undefined && a[column] !== null ? a[column].toString().toLowerCase() : '';
      const valueB = b[column] !== undefined && b[column] !== null ? b[column].toString().toLowerCase() : '';

      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'bi-sort text-muted';
    }
    return this.sortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }

  // Reset all filters and sorting
  resetFilters(): void {
    this.filterType = 'nome';
    this.searchTerm = '';
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.paginaAtual = 1;
    this.carregarTodosColaboradores();
  }

  // Métodos de paginação
  mudarPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.paginaAtual = pagina;
      this.carregarTodosColaboradores();
    }
  }

  mudarItensPorPagina(): void {
    this.paginaAtual = 1; // Voltar para a primeira página
    this.carregarTodosColaboradores();
  }

  paginasVisiveis(): number[] {
    const paginasArray: number[] = [];
    const maxBotoes = 5;
    if (this.totalPaginas <= maxBotoes) {
      for (let i = 1; i <= this.totalPaginas; i++) {
        paginasArray.push(i);
      }
    } else {
      let inicio = Math.max(1, this.paginaAtual - Math.floor(maxBotoes / 2));
      let fim = Math.min(this.totalPaginas, inicio + maxBotoes - 1);
      if (fim - inicio + 1 < maxBotoes) {
        inicio = Math.max(1, fim - maxBotoes + 1);
      }
      for (let i = inicio; i <= fim; i++) {
        paginasArray.push(i);
      }
    }
    return paginasArray;
  }

  toggleExportMenu(): void {
    this.exportMenuAberto = !this.exportMenuAberto;
  }

  exportarCSV(): void {
    this.exportIconAnimando = true;
    this.exportIconOk = false;
    setTimeout(() => {
      const linhas: string[][] = [];
      // Cabeçalho
      linhas.push([
        'Nome', 'Email', 'CPF', 'Matrícula', 'Empresa'
      ]);
      this.filteredColaboradores.forEach(colaborador => {
        linhas.push([
          colaborador.nome || '',
          colaborador.email || '',
          this.formatarCPF(colaborador.cpf),
          colaborador.matricula || '',
          colaborador.empresaNome || ''
        ]);
      });
      const csvContent = linhas.map(e => e.join(';')).join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const tempoMinimo = 1000;
      const inicio = Date.now();

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'colaboradores.csv');
      document.body.appendChild(link);

      const tempoPassado = Date.now() - inicio;
      const tempoRestante = Math.max(0, tempoMinimo - tempoPassado);

      setTimeout(() => {
        link.click();
        document.body.removeChild(link);

        this.exportIconAnimando = false;
        this.exportIconOk = true;
        setTimeout(() => this.exportIconOk = false, 1000);
      }, tempoRestante);
    }, 0);
  }

  exportarExcel(): void {
    this.exportIconAnimando = true;
    this.exportIconOk = false;
    setTimeout(() => {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Colaboradores');
      worksheet.addRow([
        'Nome', 'Email', 'CPF', 'Matrícula', 'Empresa'
      ]);
      this.filteredColaboradores.forEach(colaborador => {
        worksheet.addRow([
          colaborador.nome || '',
          colaborador.email || '',
          this.formatarCPF(colaborador.cpf),
          colaborador.matricula || '',
          colaborador.empresaNome || ''
        ]);
      });
      const tempoMinimo = 1000;
      const inicio = Date.now();
      workbook.xlsx.writeBuffer().then((buffer) => {
        const tempoPassado = Date.now() - inicio;
        const tempoRestante = Math.max(0, tempoMinimo - tempoPassado);
        setTimeout(() => {
          const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          saveAs(blob, 'colaboradores.xlsx');
          this.exportIconAnimando = false;
          this.exportIconOk = true;
          setTimeout(() => this.exportIconOk = false, 1000);
        }, tempoRestante);
      });
    }, 0);
  }
}
